import { tracked } from "@glimmer/tracking";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { popupAjaxError } from "discourse/lib/ajax-error";
import BulkSelectHelper from "discourse/lib/bulk-select-helper";
import { filterTypeForMode } from "discourse/lib/filter-mode";
import { disableImplicitInjections } from "discourse/lib/implicit-injections";
import { defineTrackedProperty } from "discourse/lib/tracked-tools";
import Topic from "discourse/models/topic";

// Just add query params here to have them automatically passed to topic list filters.
export const queryParams = {
  order: { replace: true, refreshModel: true },
  ascending: { replace: true, refreshModel: true, default: false },
  status: { replace: true, refreshModel: true },
  state: { replace: true, refreshModel: true },
  search: { replace: true, refreshModel: true },
  max_posts: { replace: true, refreshModel: true },
  min_posts: { replace: true, refreshModel: true },
  q: { replace: true, refreshModel: true },
  before: { replace: true, refreshModel: true },
  bumped_before: { replace: true, refreshModel: true },
  f: { replace: true, refreshModel: true },
  subset: { replace: true, refreshModel: true },
  period: { replace: true, refreshModel: true },
  topic_ids: { replace: true, refreshModel: true },
  group_name: { replace: true, refreshModel: true },
  tags: { replace: true, refreshModel: true },
  match_all_tags: { replace: true, refreshModel: true },
  no_subcategories: { replace: true, refreshModel: true },
  no_tags: { replace: true, refreshModel: true },
  exclude_tag: { replace: true, refreshModel: true },
};

export function resetParams(skipParams = []) {
  for (const [param, value] of Object.entries(queryParams)) {
    if (!skipParams.includes(param)) {
      this.controller.set(param, value.default);
    }
  }
}

export function addDiscoveryQueryParam(p, opts) {
  queryParams[p] = opts;
}

@disableImplicitInjections
export default class DiscoveryListController extends Controller {
  @service appEvents;
  @service composer;
  @service siteSettings;
  @service currentUser;
  @service router;
  @service store;
  @service topicTrackingState;

  @tracked model;
  @tracked showTagInfo = false;

  /** @type {import("discourse/models/tag-info").default | null} */
  @tracked tagInfo = null;
  @tracked loadingTagInfo = false;
  queryParams = Object.keys(queryParams);
  bulkSelectHelper = new BulkSelectHelper(this);
  #loadedTagId = null;
  #tagInfoFetchToken = 0;

  constructor() {
    super(...arguments);
    for (const [name, info] of Object.entries(queryParams)) {
      defineTrackedProperty(this, name, info.default);
    }

    this.bulkSelectHelper.onResetNew = () => this.resetNew();
    this.appEvents.on("tag-info:updated", this, this.onTagInfoUpdated);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.appEvents.off("tag-info:updated", this, this.onTagInfoUpdated);
  }

  get canBulkSelect() {
    return (
      this.currentUser?.canManageTopic ||
      this.showDismissRead ||
      this.showResetNew
    );
  }

  get showDismissRead() {
    return (
      filterTypeForMode(this.model.list?.filter) === "unread" &&
      this.model.list.get("topics.length") > 0
    );
  }

  get showResetNew() {
    return (
      filterTypeForMode(this.model.list?.filter) === "new" &&
      this.model.list?.get("topics.length") > 0
    );
  }

  get createTopicTargetCategory() {
    const { category } = this.model;

    if (category?.canCreateTopic) {
      return category;
    }

    return this.subcategoryWithPermission ?? category;
  }

  get subcategoryWithPermission() {
    if (this.siteSettings.default_subcategory_on_read_only_category) {
      return this.model.category?.subcategoryWithCreateTopicPermission;
    }
  }

  get createTopicDisabled() {
    // We are in a category route, but user does not have permission for the category
    return (
      !this.model.category?.canCreateTopic && !this.subcategoryWithPermission
    );
  }

  get resolvedAscending() {
    return (
      (this.ascending ?? this.model.list.get("params.ascending")) === "true"
    );
  }

  get resolvedOrder() {
    return this.order ?? this.model.list.get("params.order") ?? "activity";
  }

  get dismissNewSubset() {
    return this.model.list?.listParams?.subset ?? this.subset;
  }

  get canShowTagInfo() {
    const { tag, category, additionalTags } = this.model;
    return tag && tag.name !== "none" && !additionalTags && !category;
  }

  async callResetNew(
    dismissPosts = false,
    dismissTopics = false,
    untrack = false
  ) {
    const isTracked =
      (this.router.currentRoute.queryParams["f"] ||
        this.router.currentRoute.queryParams["filter"]) === "tracked";

    let topicIds = this.bulkSelectHelper.selected.map((topic) => topic.id);
    const result = await Topic.resetNew(
      this.model.category,
      !this.model.noSubcategories,
      {
        tracked: isTracked,
        tag: this.model.tag,
        topicIds,
        dismissPosts,
        dismissTopics,
        untrack,
      }
    );

    if (result.topic_ids) {
      this.topicTrackingState.removeTopics(result.topic_ids);
    }
    this.bulkSelectHelper.bulkSelectEnabled = false;
    this.bulkSelectHelper.clear();
    this.router.refresh();
  }

  buildResetNewOptions(overrides = {}) {
    const options = {
      dismissPosts: true,
      dismissTopics: true,
      untrack: false,
    };

    if (this.dismissNewSubset === "topics") {
      options.dismissPosts = false;
    } else if (this.dismissNewSubset === "replies") {
      options.dismissTopics = false;
    }

    return { ...options, ...overrides };
  }

  @action
  resetNew(overrides = {}) {
    if (!this.currentUser.unified_new_enabled) {
      return this.callResetNew();
    }

    const { dismissPosts, dismissTopics, untrack } =
      this.buildResetNewOptions(overrides);

    return this.callResetNew(dismissPosts, dismissTopics, untrack);
  }

  @action
  createTopic() {
    this.composer.openNewTopic({
      category: this.createTopicTargetCategory,
      tags: [this.model.tag?.name, ...(this.model.additionalTags ?? [])]
        .filter(Boolean)
        .filter((t) => !["none", "all"].includes(t))
        .join(","),
    });
  }

  @action
  changePeriod(p) {
    this.period = p;
  }

  @action
  changeSort(sortBy) {
    if (sortBy === this.resolvedOrder) {
      this.ascending = !this.resolvedAscending;
    } else {
      this.ascending = false;
    }
    this.order = sortBy;
  }

  @action
  changeNewListSubset(subset) {
    this.subset = subset;
    this.model.list.updateNewListSubsetParam(subset);
  }

  @action
  async toggleTagInfo() {
    if (this.showTagInfo) {
      this.showTagInfo = false;
      return;
    }

    const tagKey = this.model.tag?.id || this.model.tag?.name;
    if (!tagKey) {
      return;
    }

    if (!this.tagInfo || this.#loadedTagId !== tagKey) {
      try {
        await this.#fetchTagInfo(tagKey);
      } catch {
        return;
      }
    }

    this.showTagInfo = true;
  }

  @action
  onTagInfoUpdated(tagId) {
    if (this.#loadedTagId !== tagId) {
      return;
    }

    if (this.showTagInfo) {
      this.#fetchTagInfo(tagId).catch(() => {
        this.#resetTagInfo();
        this.showTagInfo = false;
      });
    } else {
      this.#resetTagInfo();
    }
  }

  @action
  syncTagInfo() {
    if (!this.canShowTagInfo) {
      this.showTagInfo = false;
      this.#resetTagInfo();
      return;
    }

    const tagKey = this.model.tag.id || this.model.tag.name;

    if (this.#loadedTagId === tagKey) {
      return;
    }

    if (this.showTagInfo) {
      this.#fetchTagInfo(tagKey).catch(() => {
        this.#resetTagInfo();
        this.showTagInfo = false;
      });
    } else {
      this.#resetTagInfo();
    }
  }

  @action
  dismissRead(dismissTopics) {
    const operationType = dismissTopics ? "topics" : "posts";

    this.bulkSelectHelper.dismissRead(operationType, {
      categoryId: this.model.category?.id,
      tagName: this.model.tag?.id,
      includeSubcategories: !this.model.noSubcategories,
    });
  }

  #resetTagInfo() {
    this.tagInfo = null;
    this.#loadedTagId = null;
    this.#tagInfoFetchToken++;
    this.loadingTagInfo = false;
  }

  async #fetchTagInfo(tagKey) {
    const token = ++this.#tagInfoFetchToken;
    this.loadingTagInfo = true;
    try {
      const result = await this.store.find("tag-info", tagKey);

      // discard if a newer fetch superseded this one
      if (token !== this.#tagInfoFetchToken) {
        return;
      }

      result.synonyms = result.synonyms.map((s) =>
        this.store.createRecord("tag", s)
      );
      this.tagInfo = result;
      this.#loadedTagId = tagKey;
    } catch (e) {
      if (token !== this.#tagInfoFetchToken) {
        return;
      }
      popupAjaxError(e);
      throw e;
    } finally {
      if (token === this.#tagInfoFetchToken) {
        this.loadingTagInfo = false;
      }
    }
  }
}
